/*
 * This file is the entry point to the VizPortal Client application. The order of imports in this
 * file is very important, because these imports all have side effects that affect how the app gets
 * initialized.
 *
 * As of fall 2020, VizPortal Client has two paradigms:
 *
 * - AngularJS (old): Each directive, controllers, and other Angular configuration registers itself
 *   into the dependency injector, so those are all listed here, and `run` (which starts the Angular
 *   app) must be last.
 *
 * - React/Redux (new): The React portion of the code is written entirely in JavaScript, so the
 *   import order matters less (because all components depend on their children via). But there are
 *   still some global initializations that must happen before the root component is rendered.
 *   (note: as Angular still exists, the root React component is currently rendered by Angular.)
 */

import './configureGlobals';
import './FeatureFlags';
import './sidecar';
import '../httpClient/utils/Interceptors';
import '../app/InitReduxStore';
import '../contentDefinitions/populateContentDefinitionLookupLists';
import '../common/react/angularDirectives';
import '../common/react/components/dataGrid/AngularToReactDataGridBridge';
import '../common/react/components/InterpolatedString';
import '../common/utilities/TableauLog';
import '../settings/components/AnalyticsExtensionsSettingsForSite';
import '../settings/controllers/SiteSettingsForAnalyticsExtensionsCtrl';
import './directives/keyEventDirectives';
import './factories/AngularContextFactory';
import './filters/ScheduleFrequencyDescriptionFilter';
import '../commonInfra/utils/SizeFilter';
import './L10n/L10n';
import './models/Set';
import '../backgroundJob/types/BackgroundJobs';
import './resources/Dataroles';
import '../revisionHistoryDatasource/resources/DatasourceRevisions';
import './resources/Lenses';
import './resources/RemoteRefreshAgents';
import './resources/RemoteRefreshSchedules';
import './resources/Schedules';
import './siteManagement/services/resources/SiteNameSansIds';
import './siteManagement/services/resources/SiteNameWithIds';
import './siteManagement/services/resources/Sites';
import './resources/WorkbookAccelerations';
import '../revisionHistoryWorkbook/resources/WorkbookRevisions';
import '../app/spaces/SpacesSiteUrls';
import '../commonInfra/contentActions/ActionHelpers';
import '../commonInfra/contentActions/ActionPanelInterfaces';
import '../commonInfra/contentActions/ContentActionNotification';
import '../datasource/services/ChangeDatasourceRemoteRefreshPropertiesHelper';
import './services/ChangeScheduleAction';
import './services/ContentUrl';
import './services/CreateOrUpdateScheduleAction';
import './siteManagement/services/CreateSiteAction';
import './services/DataRoleErrors';
import '../revisionHistory/services/DeleteRevisionAction';
import '~src/schedule/actions/DeleteSchedulesAction';
import './services/EditRemoteRefreshExpectedDurationAction';
import '../userManagement/services/EditSiteMembershipAction';
import '../httpClient/WebHttpClientWithErrorToast';
import '../httpClient/utils/HttpClientTestHook';
import './services/LicensesService';
import '../zeus/PostLoginNotification';
import '../zeus/SiteExpirationNotification';
import '../revisionHistoryWorkbook/services/PreviewWorkbookRevisionAction';
import './services/RenameAction';
import './services/RestApiService';
import '../revisionHistory/services/RevisionActions';
import '../revisionHistory/services/RevisionHistoryAction';
import '../schedule/actions/RunSchedulesAction';
import '../schedule/actions/RunServerSchedulesAction';
import './services/ScheduleErrors';
import './services/SchedulePermissions';
import '~src/schedule/actions/SetScheduleStatusAction';
import './services/SiteUrlMatcherFactory';
import './services/ToasterService';
import './services/UrlTelemetryService';
import '../dataAcceleration/actions/WorkbookAccelerationActions';
import 'focus-visible/dist/focus-visible.min.js';
import 'url-search-params-polyfill';
import './config';
import './ImagePreloader';
// Finally, after all the services are registered in Angular, we can bootstrap the app.
import './run';
